import { Controller } from "stimulus"

/**
 * RevealController
 * ================
 * Scroll-based reveal effect with sticky images and text fading.
 * Perfect for "how it works" sections where images change as you scroll through steps.
 *
 * WHAT IT DOES:
 * - Creates a scroll-linked animation where images switch based on scroll position
 * - Activates the image corresponding to whichever section is closest to viewport center
 * - Optionally fades text in sections based on their distance from viewport center
 * - Works well with CSS sticky positioning for the image panel
 *
 * BEHAVIOR:
 * - As user scrolls: The controller continuously monitors which section is closest
 *   to the center of the viewport.
 * - When a section becomes the closest: Its corresponding image (by index) gets
 *   the "is-active" class, all other images lose it. This allows CSS transitions
 *   between images (e.g., opacity, transform).
 * - Text fade effect: On larger screens, text in each section fades based on
 *   distance from viewport center. Closer = more opaque, farther = more transparent.
 *
 * HTML USAGE:
 *   <div data-controller="reveal" class="reveal-container">
 *
 *     <!-- Sticky panel with images (usually position: sticky in CSS) -->
 *     <div data-reveal-target="stickyPanel" class="reveal-sticky">
 *       <div data-reveal-target="imageContainer" class="reveal-images">
 *         <!-- Images are activated by index matching section index -->
 *         <img data-reveal-target="image" src="step1.jpg" class="reveal-image">
 *         <img data-reveal-target="image" src="step2.jpg" class="reveal-image">
 *         <img data-reveal-target="image" src="step3.jpg" class="reveal-image">
 *       </div>
 *     </div>
 *
 *     <!-- Scrollable sections (each corresponds to an image by index) -->
 *     <div class="reveal-sections">
 *       <div data-reveal-target="section" class="reveal-section">
 *         <h3 data-reveal-target="text">Step 1</h3>
 *         <p data-reveal-target="text">Description of step 1...</p>
 *       </div>
 *       <div data-reveal-target="section" class="reveal-section">
 *         <h3 data-reveal-target="text">Step 2</h3>
 *         <p data-reveal-target="text">Description of step 2...</p>
 *       </div>
 *       <div data-reveal-target="section" class="reveal-section">
 *         <h3 data-reveal-target="text">Step 3</h3>
 *         <p data-reveal-target="text">Description of step 3...</p>
 *       </div>
 *     </div>
 *   </div>
 *
 * CSS REQUIREMENTS:
 *   .reveal-image {
 *     opacity: 0;
 *     transition: opacity 0.3s ease;
 *   }
 *   .reveal-image.is-active {
 *     opacity: 1;
 *   }
 *
 * TARGETS:
 *   - stickyPanel: The container that stays sticky while scrolling (optional)
 *   - imageContainer: Container holding all images (optional)
 *   - image: Each image element (activated by adding activeClass)
 *   - section: Each content section (determines which image is active)
 *   - text: Text elements within sections that should fade (optional)
 *
 * VALUES (all optional):
 *   - activeClass: CSS class for active images (default: "is-active")
 *   - fadeRange: Viewport height multiplier for fade range (default: 0.4)
 *   - minOpacity: Minimum opacity for faded text (default: 0.3)
 *   - fadeBreakpoint: Min screen width to enable fade effect (default: 1024)
 *   - enableFade: Whether to enable text fade effect (default: true)
 */
export default class extends Controller {
  static targets = ["stickyPanel", "imageContainer", "image", "section", "text"]
  static values = {
    activeClass: String,
    fadeRange: Number,
    minOpacity: Number,
    fadeBreakpoint: Number,
    enableFade: Boolean
  }

  get activeClass() {
    return this.hasActiveClassValue ? this.activeClassValue : "is-active"
  }

  get fadeRange() {
    return this.hasFadeRangeValue ? this.fadeRangeValue : 0.4
  }

  get minOpacity() {
    return this.hasMinOpacityValue ? this.minOpacityValue : 0.3
  }

  get fadeBreakpoint() {
    return this.hasFadeBreakpointValue ? this.fadeBreakpointValue : 1024
  }

  get enableFade() {
    // Default to true if not specified
    return this.hasEnableFadeValue ? this.enableFadeValue : true
  }

  // Called when controller connects. Sets up scroll listener and initial state.
  connect() {
    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
    // Run once immediately to set initial state
    this.handleScroll()
  }

  // Called when controller disconnects. Cleans up scroll listener.
  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
  }

  // Main scroll handler. Determines which section is closest to viewport center
  // and activates the corresponding image.
  handleScroll() {
    // Need both sections and images to work
    if (!this.hasSectionTarget || !this.hasImageTarget) return

    const viewportCenter = window.innerHeight / 2
    const fadeRangePixels = window.innerHeight * this.fadeRange

    let activeIndex = 0
    let closestDistance = Infinity

    this.sectionTargets.forEach((section, index) => {
      const rect = section.getBoundingClientRect()
      const sectionCenter = rect.top + rect.height / 2
      const distance = Math.abs(sectionCenter - viewportCenter)

      // Track which section is closest to viewport center
      if (distance < closestDistance) {
        closestDistance = distance
        activeIndex = index
      }

      // Apply fade effect to text elements within this section
      if (this.enableFade && window.innerWidth >= this.fadeBreakpoint) {
        const textElements = section.querySelectorAll('[data-reveal-target="text"]')
        textElements.forEach(textContent => {
          // Opacity decreases as distance increases, with a minimum of minOpacity
          const opacity = Math.max(this.minOpacity, 1 - (distance / fadeRangePixels))
          textContent.style.opacity = opacity
        })
      }
    })

    // Update image active states
    this.imageTargets.forEach((image, index) => {
      if (index === activeIndex) {
        image.classList.add(this.activeClass)
      } else {
        image.classList.remove(this.activeClass)
      }
    })
  }
}
